import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type ScrollDirection = 'up' | 'down' | null;

interface UseScrollDirectionOptions {
  threshold?: number;
  debounceMs?: number;
  triggerElementRef?: RefObject<HTMLElement | null>;
}

const useScrollDirection = ({
  threshold = 1,
  debounceMs = 50,
  triggerElementRef,
}: UseScrollDirectionOptions = {}): ScrollDirection => {
  const BASE_OFFSET = 300;
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isTriggerElementVisible, setIsTriggerElementVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!triggerElementRef || !triggerElementRef?.current) {
      // If no trigger element, always allow tracking
      setIsTriggerElementVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTriggerElementVisible(entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '200px 0px 0px 0px',
      },
    );

    observer.observe(triggerElementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [triggerElementRef]);

  const shouldResetDirection = useCallback(() => {
    if (scrollDirection !== null) {
      setScrollDirection(null);
    }
  }, [scrollDirection]);

  const updateScrollDirection = useCallback(() => {
    if (isTriggerElementVisible) {
      shouldResetDirection();
      return;
    }

    const scrollY = window.scrollY ?? window.pageYOffset;

    if (!triggerElementRef && scrollY < BASE_OFFSET) {
      shouldResetDirection();
      return;
    }

    if (Math.abs(scrollY - lastScrollY) < threshold) {
      return;
    }

    const newDirection = scrollY > lastScrollY ? 'down' : 'up';
    if (newDirection !== scrollDirection) {
      setScrollDirection(newDirection);
    }

    setLastScrollY(scrollY);
    setIsScrolling(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, debounceMs);
  }, [
    lastScrollY,
    threshold,
    scrollDirection,
    debounceMs,
    isTriggerElementVisible,
    triggerElementRef,
    shouldResetDirection,
  ]);

  useEffect(() => {
    window.addEventListener('scroll', updateScrollDirection, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollDirection);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [updateScrollDirection]);

  return isScrolling ? scrollDirection : null;
};

export default useScrollDirection;
